import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  decideApproval,
  getApprovalQueue,
  getApprovalState,
  listApprovalSteps,
  reassignApproval,
  updateApprovalSteps,
  type ApprovalDecisionInput,
  type ApprovalState,
  type ApprovalStep,
  type FlowType,
  type PaginatedApprovalQueue,
  type ReassignInput,
  type UpdateApprovalStepInput,
} from "../api/approvals";
import { TASKS_KEY } from "./useTasks";

export const APPROVALS_KEY = "approvals";
export const APPROVAL_STEPS_KEY = "approval-steps";

export function useApprovalQueue(
  query: { flowType?: FlowType; limit?: number; offset?: number } = {},
  enabled = true,
) {
  return useQuery<PaginatedApprovalQueue>({
    queryKey: [APPROVALS_KEY, "queue", query],
    queryFn: () => getApprovalQueue(query),
    enabled,
    staleTime: 30_000,
    retry: false,
  });
}

export function useApprovalState(taskId: string | null | undefined, enabled = true) {
  return useQuery<ApprovalState>({
    queryKey: [APPROVALS_KEY, "state", taskId],
    queryFn: () => getApprovalState(taskId as string),
    enabled: !!taskId && enabled,
    retry: false,
  });
}

function useApprovalMutationInvalidator() {
  const qc = useQueryClient();
  return (taskId: string) => {
    void qc.invalidateQueries({ queryKey: [APPROVALS_KEY, "state", taskId] });
    void qc.invalidateQueries({ queryKey: [APPROVALS_KEY, "queue"] });
    void qc.invalidateQueries({ queryKey: [TASKS_KEY, "detail", taskId] });
    void qc.invalidateQueries({ queryKey: [TASKS_KEY, "queue"] });
  };
}

export function useDecideApproval() {
  const invalidate = useApprovalMutationInvalidator();
  return useMutation<ApprovalState, Error, { taskId: string; body: ApprovalDecisionInput }>({
    mutationFn: ({ taskId, body }) => decideApproval(taskId, body),
    onSuccess: (_data, vars) => invalidate(vars.taskId),
  });
}

export function useReassignApproval() {
  const invalidate = useApprovalMutationInvalidator();
  return useMutation<ApprovalState, Error, { taskId: string; body: ReassignInput }>({
    mutationFn: ({ taskId, body }) => reassignApproval(taskId, body),
    onSuccess: (_data, vars) => invalidate(vars.taskId),
  });
}

export function useApprovalSteps(enabled = true) {
  return useQuery<ApprovalStep[]>({
    queryKey: [APPROVAL_STEPS_KEY, "list"],
    queryFn: () => listApprovalSteps(),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

export function useUpdateApprovalSteps() {
  const qc = useQueryClient();
  return useMutation<ApprovalStep[], Error, UpdateApprovalStepInput[]>({
    mutationFn: (items) => updateApprovalSteps(items),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [APPROVAL_STEPS_KEY] });
    },
  });
}
